import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Crosshair, ArrowRight, MapPin, Boxes, History, X } from "lucide-react";
import { toast } from "sonner";
import { generateReport } from "@/lib/api";
import ReportLoading from "@/components/report/ReportLoading";
import ReportView from "@/components/report/ReportView";

const RECENT_KEY = "ds_recent_searches";

export default function Report() {
  const [location, setLocation] = useState("");
  const [industry, setIndustry] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const [report, setReport] = useState(null);
  const [recent, setRecent] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const didInit = useRef(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
      if (Array.isArray(saved)) setRecent(saved);
    } catch {
      setRecent([]);
    }
  }, []);

  const saveRecent = useCallback((loc, ind) => {
    setRecent((prev) => {
      const next = [{ location: loc, industry: ind }, ...prev.filter(
        (r) => !(r.location.toLowerCase() === loc.toLowerCase() && r.industry.toLowerCase() === ind.toLowerCase())
      )].slice(0, 6);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const run = useCallback(async (loc, ind) => {
    if (!loc.trim() || !ind.trim()) {
      toast.error("Enter a location and an industry to lock on.");
      return;
    }
    setLocation(loc);
    setIndustry(ind);
    setStatus("loading");
    setReport(null);
    try {
      const data = await generateReport(loc.trim(), ind.trim());
      setReport(data);
      setStatus("done");
      saveRecent(loc.trim(), ind.trim());
      setSearchParams({ location: loc.trim(), industry: ind.trim() }, { replace: true });
    } catch (err) {
      setStatus("idle");
      toast.error("Signal lost — couldn't generate the report. Try again.");
    }
  }, [saveRecent, setSearchParams]);

  // Auto-run from a shared URL (?location=...&industry=...) once on mount
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    const loc = searchParams.get("location");
    const ind = searchParams.get("industry");
    if (loc && ind) run(loc, ind);
  }, [searchParams, run]);

  const clearRecent = () => {
    setRecent([]);
    try { localStorage.removeItem(RECENT_KEY); } catch {}
  };

  return (
    <div className="relative min-h-screen bg-[#050505] text-white antialiased">
      <div className="ds-grain" aria-hidden />

      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#050505]/75 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="report-logo" className="flex items-center gap-2 text-white tracking-tight">
            <span className="relative inline-flex items-center justify-center w-7 h-7 border border-[#FF5000]">
              <Crosshair className="w-4 h-4 text-[#FF5000]" />
              <span className="absolute -top-[3px] -left-[3px] w-1.5 h-1.5 bg-[#FF5000]" />
            </span>
            <span className="font-semibold text-[15px]">
              Data<span className="text-[#FF5000]">Sniper</span>
            </span>
            <span className="ml-2 text-[10px] uppercase tracking-[0.25em] text-[#FF5000] border border-[#FF5000]/40 px-1.5 py-0.5">Beta</span>
          </Link>
          <Link
            to="/"
            data-testid="report-request-access-btn"
            data-cta="true"
            className="inline-flex items-center gap-2 bg-[#FF5000] hover:bg-[#FF7333] text-white text-[13px] font-medium px-4 py-2 transition-all duration-200"
          >
            <span className="w-1.5 h-1.5 bg-white ds-pulse" />
            Join the Community
          </Link>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-24">
        <div className="max-w-3xl mb-10 ds-fade-up">
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#FF5000] mb-4">· DataSniper Beta · Generate report</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            Run a market intel sweep.
          </h1>
          <p className="mt-5 text-neutral-400 text-base md:text-[17px] leading-relaxed">
            Type a location and your inquiry. DataSniper collects live signals from Google Trends, Google News and
            GDELT, then runs an AI analysis relating to your inquiry with the data pulled... Trends.. Demands..
            Opportunities... Oh and it's free!
          </p>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); run(location, industry); }}
          data-testid="report-form"
          className="border border-neutral-800 bg-[#080808] flex flex-col md:flex-row md:items-stretch divide-y md:divide-y-0 md:divide-x divide-neutral-800"
        >
          <label className="flex-1 p-5 group cursor-text">
            <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
              <MapPin className="w-3.5 h-3.5 text-[#FF5000]" /> Location
            </span>
            <input
              data-testid="report-location-input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              disabled={status === "loading"}
              placeholder="e.g. Austin, Texas"
              className="w-full bg-transparent text-white text-[15px] placeholder:text-neutral-600 focus:outline-none"
            />
          </label>
          <label className="flex-[1.4] p-5 group cursor-text">
            <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
              <Boxes className="w-3.5 h-3.5 text-[#FF5000]" /> Industry · product · service
            </span>
            <input
              data-testid="report-industry-input"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              disabled={status === "loading"}
              placeholder="e.g. cold plunge tubs"
              className="w-full bg-transparent text-white text-[15px] placeholder:text-neutral-600 focus:outline-none"
            />
          </label>
          <button
            type="submit"
            data-testid="report-generate-btn"
            data-cta="true"
            disabled={status === "loading"}
            className="group inline-flex items-center justify-center gap-3 bg-[#FF5000] hover:bg-[#FF7333] disabled:opacity-50 disabled:cursor-wait text-white text-[14px] font-medium px-8 py-5 md:py-0 transition-all duration-200 whitespace-nowrap"
          >
            {status === "loading" ? "Sniping…" : "Generate Report"}
            {status !== "loading" && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
          </button>
        </form>

        {/* Recent searches (stored locally in the browser) */}
        {recent.length > 0 && status !== "loading" && (
          <div data-testid="recent-searches" className="mt-5 flex flex-wrap items-center gap-2.5">
            <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-neutral-500 mr-1">
              <History className="w-3.5 h-3.5" /> Recent
            </span>
            {recent.map((r) => (
              <button
                key={`${r.location}-${r.industry}`}
                data-testid="recent-chip"
                onClick={() => run(r.location, r.industry)}
                className="text-[12px] text-neutral-300 hover:text-white border border-neutral-800 hover:border-[#FF5000]/60 px-3 py-1.5 transition-colors"
              >
                <span className="text-[#FF5000]">{r.industry}</span> · {r.location}
              </button>
            ))}
            <button
              data-testid="recent-clear"
              onClick={clearRecent}
              className="inline-flex items-center gap-1 text-[11px] text-neutral-600 hover:text-neutral-300 transition-colors"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          </div>
        )}

        <div className="mt-10">
          {status === "loading" && <ReportLoading />}
          {status === "done" && report && <ReportView report={report} onReset={() => { setStatus("idle"); setReport(null); }} />}
        </div>
      </main>
    </div>
  );
}
